import { api } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   What the IG Suicide pages read from /api/m/igsuicide, typed once. The
   shapes mirror server/report.ts and server/routes.ts; the web half never
   imports the server half, so they are written out here by hand.
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/igsuicide";

export type Totals = { followers: number | null; views: number | null; measured: number };

export type Growth = { followers: number | null; views: number | null; measuredFrom: string | null; measuredTo: string | null; partial: boolean };

export type AccountState = "active" | "missing" | "banned" | "other";

export type Account = {
  id: string;
  handle: string;
  sheetHandle: string;
  creator: string | null;
  handler: string | null;
  /** Its poster in Signet, else the sheet's handler. */
  poster: string | null;
  /** "Warm-up day 2 of 3" or "Posting day 1 of 3" while it is in those days, else null. */
  day: string | null;
  state: AccountState;
  sheetStatus: string | null;
  followers: number | null;
  startFollowers: number | null;
  startDay: string | null;
  views: number | null;
  reels: number | null;
  isPrivate: boolean;
  photo: boolean;
  growth: Growth;
  previous: { followers: number | null; views: number | null };
  lastOkAt: string | null;
  missingSince: string | null;
  bannedAt: string | null;
  banReason: BanReason | null;
};

export type BanReason = "connected" | "three_reasons";
export const BAN_REASON: Record<BanReason, string> = { connected: "Connected ban", three_reasons: "3 reason ban" };

export type CreatorSummary = { creator: string; active: number; banned: number; growth: Totals; previous: Totals; followers: number };

export type Overview = {
  window: { from: string; to: string };
  previousWindow: { from: string; to: string };
  totals: Totals;
  previous: Totals;
  active: number;
  missing: number;
  bannedInWindow: number;
  firstReading: string | null;
  accounts: Account[];
  banned: Account[];
  bannedAll: Account[];
  creators: CreatorSummary[];
  today: string;
  sheetUrl: string;
};

export type Status = {
  sheet: { syncedAt: string | null; accounts: number | null; problem: string | null; off?: boolean };
  scan: { due: number; readThisWeek: number; source: string | null; requestsToday: number };
};

export type ReviewAccount = {
  id: string;
  handle: string;
  creator: string | null;
  handler: string | null;
  followers: number | null;
  missingSince: string | null;
  lastOkAt: string | null;
  keptBefore: string | null;
  appealedAt: string | null;
  photo: boolean;
  /** A note left on the Ban check during this absence: the row says it was checked. */
  check: { note: string; at: string; by: string | null } | null;
};

export type Review = { accounts: ReviewAccount[]; unsent: { id: string; handle: string; creator: string | null; error: string | null }[] };

export const keys = {
  all: ["igsuicide"] as const,
  overview: (from: string | null, to: string | null, creator: string | null) => ["igsuicide", "overview", from, to, creator] as const,
  status: ["igsuicide", "status"] as const,
  review: ["igsuicide", "review"] as const,
  setup: ["igsuicide", "setup"] as const,
};

/** A creator's row on the Setup tab (2026-10-22). current is their live accounts on phones. */
/** The names are the links' Drive folder names, when the shared Google account can see them. */
export type CreatorSetup = { creator: string; duplicationLink: string | null; uniqueLink: string | null; duplicationName: string | null; uniqueName: string | null; required: number | null; current: number };
export const fetchSetup = () => api<{ creators: CreatorSetup[] }>(`${BASE}/setup`);
export const saveSetup = (creator: string, change: { duplicationLink?: string | null; uniqueLink?: string | null; required?: number | null }) =>
  api<{ ok: true }>(`${BASE}/setup/${encodeURIComponent(creator)}`, { method: "PATCH", body: change });

export function fetchOverview(from: string | null, to: string | null, creator: string | null): Promise<Overview> {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (creator) params.set("creator", creator);
  return api<Overview>(`${BASE}/overview?${params}`);
}

export const fetchStatus = () => api<Status>(`${BASE}/status`);
export const fetchReview = () => api<Review>(`${BASE}/review`);
export const markBanned = (id: string, ban: { reason: BanReason; on: string }) =>
  api<{ ok: true; sheet: "written" | "pending" | "off"; problem: string | null }>(`${BASE}/accounts/${encodeURIComponent(id)}/banned`, { method: "POST", body: ban });
export const saveCheckNote = (id: string, note: string) => api<{ ok: true }>(`${BASE}/accounts/${encodeURIComponent(id)}/check-note`, { method: "POST", body: { note } });
export const markNotBanned = (id: string) => api<{ ok: true }>(`${BASE}/accounts/${encodeURIComponent(id)}/not-banned`, { method: "POST", body: {} });
export const trendUrl = (id: string) => `${BASE}/accounts/${encodeURIComponent(id)}/trend`;
export const photoUrl = (id: string) => `${BASE}/photo/${encodeURIComponent(id)}`;
export const instagramProfile = (handle: string) => `https://www.instagram.com/${encodeURIComponent(handle)}/`;

export const str = (value: unknown) => (typeof value === "string" && value ? value : undefined);

/** "14 Sep" or "14 Sep 2025" when it is not this year. */
export function shortDay(day: string, thisYear = new Date().getFullYear()): string {
  const date = new Date(`${day}T00:00:00`);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(date.getFullYear() !== thisYear ? { year: "numeric" } : {}) });
}

export function signed(n: number | null, format: (n: number) => string): string {
  if (n === null) return "";
  return n > 0 ? `+${format(n)}` : format(n);
}
